import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Save, AddCircleOutline, Edit, Delete } from '@mui/icons-material';
import { RootState } from 'src/redux-store/appStore';
import { useDispatch, useSelector } from 'react-redux';
import { updateChimney } from 'src/redux-store/slices/constructionFormSlice';
import toast from 'react-hot-toast';
import { httpRequest } from 'src/services/httpRequest';
import { useParams } from 'react-router-dom';

// Define type for common fields
interface BaseItem {
  check: boolean;
}

// Device item interface
interface DeviceItem extends BaseItem {
  id?: number;
  isEditing?: boolean;
  Appliance: string;
  FuelType: string;
  BTURating: number;
}

// Work item interface
interface WorkItem extends BaseItem {
  Item: string;
  Cost: number;
  Type?: string;
  // Optional fields
  name?: string;
  Size?: number;
  Quantity?: number;
  WallArea?: string | null;
  FenceHeight?: string | null;
  SignType?: string | null;
  SignArea?: string | null;
  PoolItem?: string | null;
  PoolArea?: string | null;
  WallType?: string | null;
  RoofReplacementType?: string | null;
}

// Component props interface
interface CostOfWorkProps {
  ReplacementType: number;
  ExistingVentOrChimneySize: number;
  onBack: () => void;
  onNext: () => void;
  townshipID: number;
}

// Constants
const FUEL_TYPES = [
  'Natural Gas',
  'Propane',
  'Oil',
  'Wood',
  'Coal',
  'Electric',
  'Other'
];

const REPLACEMENT_TYPES = [
  'Oil to Gas Conversion',
  'Gas to Oil Conversion',
  'Gas Appliance Replacement',
  'Oil to Oil Replacement',
  'Other'
];

const DEFAULT_DEVICE_ITEM: DeviceItem = {
  Appliance: '',
  FuelType: '',
  BTURating: 0,
  check: true
};

const DEFAULT_OTHER_WORK_ITEM: WorkItem = {
  Item: '',
  check: true,
  Cost: 0,
  Type: 'other'
};

const CostOfWork: React.FC<CostOfWorkProps> = ({
  ReplacementType,
  ExistingVentOrChimneySize,
  onBack,
  onNext,
  townshipID
}) => {
  const dispatch = useDispatch();
  const {townshipId}= useParams();
  
  // State for form inputs
  const [deviceForm, setDeviceForm] = useState<DeviceItem>({
    ...DEFAULT_DEVICE_ITEM
  });
  const [otherWorkItemForm, setOtherWorkItemForm] = useState<WorkItem>({
    ...DEFAULT_OTHER_WORK_ITEM
  });

  // State for lists of items
  const [deviceItems, setDeviceItems] = useState<DeviceItem[]>([]);
  const [regularWorkItems, setRegularWorkItems] = useState<WorkItem[]>([]);

  // Get existing data from Redux store
  const workItems = useSelector(
    (state: RootState) => state.constructionFormSlice?.Chimney?.WorkItems || []
  );

  // Load data when component mounts
  useEffect(() => {
    if (workItems && workItems.length > 0) {
      const devices: DeviceItem[] = [];
      const regular: WorkItem[] = [];

      workItems.forEach((item: any) => {
        if (item.Appliance) {
          // This is a device item
          devices.push({
            id: devices.length + 1,
            Appliance: item.Appliance || '',
            FuelType: item.FuelType || '',
            BTURating: item.BTURating || 0,
            check: true
          });
        } else {
          // This is a regular work item
          regular.push(item as WorkItem);
        }
      });

      setDeviceItems(devices);
      setRegularWorkItems(regular);
    }
  }, [workItems]);

  // Handle input changes for device form
  const handleDeviceInputChange = (
    e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
  ) => {
    const { name, value } = e.target;
    if (name) {
      setDeviceForm((prev) => ({
        ...prev,
        [name]: name === 'BTURating' ? Number(value) : value
      }));
    }
  };

  // Handle select changes for device form
  const handleDeviceSelectChange = (field: keyof DeviceItem, value: string) => {
    setDeviceForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  // Add or update device item
  const handleAddOrUpdateDevice = () => {
    // Validate input
    if (!deviceForm.Appliance) {
      toast.error('Please enter an appliance name');
      return;
    }

    // If editing an existing item
    if (deviceForm.id !== undefined) {
      setDeviceItems((prev) =>
        prev.map((item) =>
          item.id === deviceForm.id ? { ...deviceForm, isEditing: false } : item
        )
      );
    } else {
      // Adding a new item
      const newItem: DeviceItem = {
        ...deviceForm,
        id: deviceItems.length + 1
      };
      setDeviceItems((prev) => [...prev, newItem]);
    }

    // Reset form
    setDeviceForm({ ...DEFAULT_DEVICE_ITEM });
  };

  // Edit a device item
  const handleEditDevice = (item: DeviceItem) => {
    setDeviceForm({
      ...item,
      isEditing: true
    });
  };

  // Delete a device item
  const handleDeleteDevice = (id: number) => {
    setDeviceItems((prev) => prev.filter((item) => item.id !== id));
  };

  // Handle work item changes
  const handleWorkItemChange = (
    index: number,
    field: keyof WorkItem,
    value: string | boolean | number
  ) => {
    const updatedWorkItems = [...regularWorkItems];
    updatedWorkItems[index] = { ...updatedWorkItems[index], [field]: value };
    setRegularWorkItems(updatedWorkItems);
  };

  // Add new work item
  const handleAddWorkItem = () => {
    if (otherWorkItemForm.Item.trim() === '') {
      toast.error('Please enter an item name');
      return;
    }

    const newItem: WorkItem = { ...otherWorkItemForm };
    setRegularWorkItems([...regularWorkItems, newItem]);
    setOtherWorkItemForm({ ...DEFAULT_OTHER_WORK_ITEM });
  };

  // Fetch chimney fees
  const getChimneyFees = async () => {
    // Combine device items and regular work items for the API call
    const combinedWorkItems = [...regularWorkItems, ...deviceItems];

    const payload = {
      UseGroup: 'R-3, R-5',
      CertificateType: 'Clearance',
      ReplacementType,
      WorkItems: combinedWorkItems,
      TownshipID: townshipId || townshipID,
    };

    try {
      const response = await httpRequest(
        'POST',
        `/construction/chimney/get_chimney_fees`,
        payload
      );

      if (response?.status === 'success') {
        const data = response?.data || '';
        const dataDetails = {
          'Chimney Size': data['Chimney Size'],
          'Certification Cost': data['Certification Cost'],
          totalFee: data.totalFee,
          summary: data.summary
        };

        dispatch(
          updateChimney({
            getAllFee: dataDetails
          })
        );
        toast.success('Chimney fees retrieved successfully');
      } else {
        toast.error(response?.message || 'Failed to retrieve chimney fees');
      }
    } catch (error) {
      toast.error('An error occurred while fetching chimney fees');
      console.error(error);
    }
  };

  // Save handler
  const handleSave = async () => {
    // Combine device items and regular work items for Redux
    const combinedWorkItems = [...regularWorkItems, ...deviceItems];

    // Save to Redux
    dispatch(
      updateChimney({
        WorkItems: combinedWorkItems
      })
    );

    // Fetch chimney fees
    await getChimneyFees();

    // Move to next step
    onNext();
  };

  return (
    <Grid container spacing={3}>
      {/* Left Grid - Estimation fields */}
      <Grid item md={3} sm={12} xs={12}>
        {/* Existing Vent/Chimney */}
        <Typography variant="h5">Existing Vent/Chimney</Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            mt: 2,
            mb: 4
          }}
        >
          <TextField
            type="number"
            fullWidth
            value={Number(ExistingVentOrChimneySize)}
            name="ExistingVentOrChimneySize"
            onChange={(e) =>
              dispatch(
                updateChimney({ ExistingVentOrChimneySize: e.target.value })
              )
            }
            label={
              <span>
                Size<span style={{ color: 'red' }}>*</span>
              </span>
            }
            variant="outlined"
          />
        </Box>

        {/* Replacement Type Section */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
          <Typography variant="h5">Replacement Type(s)</Typography>

          {/* Device Selection */}
          <FormControl fullWidth>
            <InputLabel>Select Replacement Type</InputLabel>
            <Select
              value={ReplacementType}
              label="Select Replacement Type"
              onChange={(e) =>
                dispatch(updateChimney({ ReplacementType: e.target.value }))
              }
            >
              {REPLACEMENT_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Device Input Form */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="h5">Appliances</Typography>

          {/* New Appliance Field */}
          <TextField
            name="Appliance"
            label="Appliance Type"
            fullWidth
            value={deviceForm.Appliance}
            onChange={handleDeviceInputChange}
          />

          {/* New Fuel Type Select Field */}
          <FormControl fullWidth>
            <InputLabel>Fuel Type</InputLabel>
            <Select
              name="FuelType"
              label="Fuel Type"
              value={deviceForm.FuelType}
              onChange={(e) =>
                handleDeviceSelectChange('FuelType', e.target.value as string)
              }
            >
              {FUEL_TYPES.map((fuel) => (
                <MenuItem key={fuel} value={fuel}>
                  {fuel}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* New BTU Rating Field */}
          <TextField
            name="BTURating"
            label="BTU Rating (input/hour)"
            type="number"
            fullWidth
            value={deviceForm.BTURating}
            onChange={handleDeviceInputChange}
            inputProps={{ min: 0 }}
          />

          <Button
            variant="contained"
            color={deviceForm.id !== undefined ? 'secondary' : 'primary'}
            startIcon={
              deviceForm.id !== undefined ? <Save /> : <AddCircleOutline />
            }
            onClick={handleAddOrUpdateDevice}
            disabled={!deviceForm.Appliance}
          >
            {deviceForm.id !== undefined ? 'Update' : 'Add'}
          </Button>
        </Box>
      </Grid>

      <Grid
        item
        md={1}
        sm={12}
        xs={12}
        sx={{ display: { xs: 'none', md: 'block' } }}
      >
        <Divider
          orientation="vertical"
          variant="middle"
          flexItem
          sx={{ height: '100%' }}
        />
      </Grid>

      {/* Right Grid - Device and Work Items */}
      <Grid item md={8} sm={12} xs={12}>
        {/* Check all Vent/Chimney Types that apply: Grid */}
        <Typography variant="h5" sx={{ mb: 2 }}>
          Check all Vent/Chimney Types that apply:
        </Typography>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {regularWorkItems?.map((item, index) => (
            <Grid item xs={12} md={4} sm={6} key={`${item.Item}-${index}`}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={item.check}
                    onChange={(e) =>
                      handleWorkItemChange(index, 'check', e.target.checked)
                    }
                  />
                }
                label={item.Item}
              />
            </Grid>
          ))}
        </Grid>

        {/* Add More Items */}
        <Typography variant="h5" sx={{ mb: 2 }}>
          Add More Items
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            mb: 4
          }}
        >
          <TextField
            name="Item"
            label="Other item name"
            fullWidth
            value={otherWorkItemForm.Item}
            onChange={(e) =>
              setOtherWorkItemForm({
                ...otherWorkItemForm,
                Item: e.target.value
              })
            }
          />

          <Button
            variant="contained"
            color="primary"
            startIcon={<AddCircleOutline />}
            onClick={handleAddWorkItem}
            disabled={!otherWorkItemForm.Item}
            sx={{ minWidth: { xs: '100%', sm: '200px' } }}
          >
            Add Item
          </Button>
        </Box>

        {/* Devices Table */}
        <Typography variant="h5" sx={{ mb: 2 }}>
          Installed Devices
        </Typography>
        <TableContainer component={Paper} sx={{ mb: 4 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Appliance</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Fuel Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>BTU Rating</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deviceItems.length > 0 ? (
                deviceItems.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>{item.Appliance}</TableCell>
                    <TableCell>{item.FuelType}</TableCell>
                    <TableCell>{item.BTURating}</TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <IconButton
                        color="primary"
                        onClick={() => handleEditDevice(item)}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteDevice(item.id!)}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No devices added yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      {/* Form Action Buttons */}
      <Grid item xs={12}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            mt: 3
          }}
        >
          <Button onClick={onBack} variant="contained" color="inherit">
            Back
          </Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Save
          </Button>
        </Box>
      </Grid>
    </Grid>
  );
};

export default CostOfWork;
